import * as React$1 from "react";
import * as React from "react";
import { TRPCUntypedClient, getUntypedClient } from "@trpc/client";
import { callTRPCProcedure, createTRPCRecursiveProxy } from "@trpc/server";
import { hashKey, infiniteQueryOptions, queryOptions, skipToken } from "@tanstack/react-query";
import { isAsyncIterable, isFunction, isObject } from "@trpc/server/unstable-core-do-not-import";
import { jsx } from "react/jsx-runtime";

//#region src/internals/utils.ts
/**
* @internal
*/
function createTRPCOptionsResult(value) {
	const path = value.path.join(".");
	return { path };
}
/**
* @internal
*/
function getClientArgs(queryKey, opts, infiniteParams) {
	const path = queryKey[0];
	let input = queryKey[1]?.input;
	if (infiniteParams) input = {
		...input ?? {},
		...infiniteParams.pageParam !== void 0 ? { cursor: infiniteParams.pageParam } : {},
		direction: infiniteParams.direction
	};
	return [
		path.join("."),
		input,
		opts?.trpc
	];
}
/**
* @internal
*/
async function buildQueryFromAsyncIterable(asyncIterable, queryClient, queryKey) {
	const queryCache = queryClient.getQueryCache();
	const query = queryCache.build(queryClient, { queryKey });
	query.setState({
		data: [],
		status: "success"
	});
	const aggregate = [];
	for await (const value of asyncIterable) {
		aggregate.push(value);
		query.setState({ data: [...aggregate] });
	}
	return aggregate;
}
/**
* To allow easy interactions with groups of related queries, such as
* invalidating all queries of a router, we use an array as the path when
* storing in tanstack query.
*
* @internal
*/
function getQueryKeyInternal(path, input, type) {
	const splitPath = path.flatMap((part) => part.split("."));
	if (!input && (!type || type === "any")) return splitPath.length ? [splitPath] : [];
	if (type === "infinite" && isObject(input) && ("direction" in input || "cursor" in input)) {
		const { cursor: _, direction: __,...inputWithoutCursorAndDirection } = input;
		return [splitPath, {
			input: inputWithoutCursorAndDirection,
			type: "infinite"
		}];
	}
	return [splitPath, {
		...typeof input !== "undefined" && input !== skipToken && { input },
		...type && type !== "any" && { type }
	}];
}
/**
* @internal
*/
function getMutationKeyInternal(path) {
	const splitPath = path.flatMap((part) => part.split("."));
	return splitPath.length ? [splitPath] : [];
}
/**
* @internal
*/
function unwrapLazyArg(valueOrLazy) {
	return isFunction(valueOrLazy) ? valueOrLazy() : valueOrLazy;
}

//#endregion
//#region src/internals/infiniteQueryOptions.ts
function trpcInfiniteQueryOptions(args) {
	const { input, query, path, queryKey, opts } = args;
	const inputIsSkipToken = input === skipToken;
	const queryFn = async (queryFnContext) => {
		const actualOpts = {
			...opts,
			trpc: {
				...opts?.trpc,
				...opts?.trpc?.abortOnUnmount ? { signal: queryFnContext.signal } : { signal: null }
			}
		};
		const result = await query(...getClientArgs(queryKey, actualOpts, {
			direction: queryFnContext.direction,
			pageParam: queryFnContext.pageParam
		}));
		return result;
	};
	return Object.assign(infiniteQueryOptions({
		...opts,
		queryKey,
		queryFn: inputIsSkipToken ? skipToken : queryFn,
		initialPageParam: opts?.initialCursor ?? input?.cursor
	}), { trpc: createTRPCOptionsResult({ path }) });
}

//#endregion
//#region src/internals/mutationOptions.ts
/**
* @internal
*/
function trpcMutationOptions(args) {
	const { mutate, path, opts, overrides } = args;
	const queryClient = unwrapLazyArg(args.queryClient);
	const mutationKey = getMutationKeyInternal(path);
	const defaultOpts = queryClient.defaultMutationOptions(queryClient.getMutationDefaults(mutationKey));
	const mutationSuccessOverride = overrides?.onSuccess ?? ((options) => options.originalFn());
	const mutationFn = async (input) => {
		const result = await mutate(...getClientArgs([path, { input }], opts));
		return result;
	};
	return {
		...opts,
		mutationKey,
		mutationFn,
		onSuccess(...args$1) {
			const originalFn = () => opts?.onSuccess?.(...args$1) ?? defaultOpts?.onSuccess?.(...args$1);
			return mutationSuccessOverride({
				originalFn,
				queryClient,
				meta: opts?.meta ?? defaultOpts?.meta ?? {}
			});
		},
		trpc: createTRPCOptionsResult({ path })
	};
}

//#endregion
//#region src/internals/queryOptions.ts
/**
* @internal
*/
function trpcQueryOptions(args) {
	const { input, query, path, queryKey, opts } = args;
	const queryClient = unwrapLazyArg(args.queryClient);
	const inputIsSkipToken = input === skipToken;
	const queryFn = async (queryFnContext) => {
		const actualOpts = {
			...opts,
			trpc: {
				...opts?.trpc,
				...opts?.trpc?.abortOnUnmount ? { signal: queryFnContext.signal } : { signal: null }
			}
		};
		const queryKey$1 = queryFnContext.queryKey;
		const result = await query(...getClientArgs(queryKey$1, actualOpts));
		if (isAsyncIterable(result)) return buildQueryFromAsyncIterable(result, queryClient, queryKey$1);
		return result;
	};
	return Object.assign(queryOptions({
		...opts,
		queryKey,
		queryFn: inputIsSkipToken ? skipToken : queryFn
	}), { trpc: createTRPCOptionsResult({ path }) });
}

//#endregion
//#region src/internals/subscriptionOptions.ts
/**
* @internal
*/
const trpcSubscriptionOptions = (args) => {
	const { subscribe, path, queryKey, opts = {} } = args;
	const input = queryKey[1]?.input;
	const enabled = "enabled" in opts ? !!opts.enabled : input !== skipToken;
	const _subscribe = (innerOpts) => {
		return subscribe(path.join("."), input ?? void 0, innerOpts);
	};
	return {
		...opts,
		enabled,
		subscribe: _subscribe,
		queryKey,
		trpc: createTRPCOptionsResult({ path })
	};
};
function useSubscription(opts) {
	const optsRef = React$1.useRef(opts);
	optsRef.current = opts;
	const trackedProps = React$1.useRef(new Set([]));
	const addTrackedProp = React$1.useCallback((key) => {
		trackedProps.current.add(key);
	}, []);
	const currentSubscriptionRef = React$1.useRef(() => {});
	const reset = React$1.useCallback(() => {
		currentSubscriptionRef.current?.();
		updateState(getInitialState);
		if (!opts.enabled) return;
		const subscription = opts.subscribe({
			onStarted: () => {
				optsRef.current.onStarted?.();
				updateState((prev) => ({
					...prev,
					status: "pending",
					error: null
				}));
			},
			onData: (data) => {
				optsRef.current.onData?.(data);
				updateState((prev) => ({
					...prev,
					status: "pending",
					data,
					error: null
				}));
			},
			onError: (error) => {
				optsRef.current.onError?.(error);
				updateState((prev) => ({
					...prev,
					status: "error",
					error
				}));
			},
			onConnectionStateChange: (result) => {
				optsRef.current.onConnectionStateChange?.(result);
				updateState((prev) => {
					switch (result.state) {
						case "connecting": return {
							...prev,
							status: "connecting",
							error: result.error
						};
						case "pending": return prev;
						case "idle": return {
							...prev,
							status: "idle",
							data: void 0,
							error: null
						};
					}
				});
			}
		});
		currentSubscriptionRef.current = () => {
			subscription.unsubscribe();
		};
	}, [hashKey(opts.queryKey), opts.enabled]);
	const getInitialState = React$1.useCallback(() => {
		return opts.enabled ? {
			data: void 0,
			error: null,
			status: "connecting",
			reset
		} : {
			data: void 0,
			error: null,
			status: "idle",
			reset
		};
	}, [opts.enabled, reset]);
	const resultRef = React$1.useRef(getInitialState());
	const [state, setState] = React$1.useState(trackResult(resultRef.current, addTrackedProp));
	state.reset = reset;
	const updateState = React$1.useCallback((callback) => {
		const prev = resultRef.current;
		const next = resultRef.current = callback(prev);
		let shouldUpdate = false;
		for (const key of trackedProps.current) if (prev[key] !== next[key]) {
			shouldUpdate = true;
			break;
		}
		if (shouldUpdate) setState(trackResult(next, addTrackedProp));
	}, [addTrackedProp]);
	React$1.useEffect(() => {
		if (!opts.enabled) return;
		reset();
		return () => {
			currentSubscriptionRef.current?.();
		};
	}, [reset, opts.enabled]);
	return state;
}
function trackResult(result, onTrackResult) {
	const trackedResult = new Proxy(result, { get(target, prop) {
		onTrackResult(prop);
		return target[prop];
	} });
	return trackedResult;
}

//#endregion
//#region src/internals/createOptionsProxy.ts
/**
* Create a typed proxy from your router types. Can also be used on the server.
*
* @see https://trpc.io/docs/client/tanstack-react-query/setup#3b-setup-without-react-context
* @see https://trpc.io/docs/client/tanstack-react-query/server-components#5-create-a-trpc-caller-for-server-components
*/
function createTRPCOptionsProxy(opts) {
	const callIt = (type) => {
		return (path, input, trpcOpts) => {
			if ("router" in opts) return Promise.resolve(unwrapLazyArg(opts.ctx)).then((ctx) => callTRPCProcedure({
				router: opts.router,
				path,
				getRawInput: async () => input,
				ctx,
				type,
				signal: void 0
			}));
			const untypedClient = opts.client instanceof TRPCUntypedClient ? opts.client : getUntypedClient(opts.client);
			return untypedClient[type](path, input, trpcOpts);
		};
	};
	return createTRPCRecursiveProxy(({ args, path: _path }) => {
		const path = [..._path];
		const utilName = path.pop();
		const [arg1, arg2] = args;
		const contextMap = {
			"~types": void 0,
			pathKey: () => {
				return getQueryKeyInternal(path);
			},
			pathFilter: () => {
				return {
					...arg1,
					queryKey: getQueryKeyInternal(path)
				};
			},
			queryOptions: () => {
				return trpcQueryOptions({
					input: arg1,
					opts: arg2,
					path,
					queryClient: opts.queryClient,
					queryKey: getQueryKeyInternal(path, arg1, "query"),
					query: callIt("query")
				});
			},
			queryKey: () => {
				return getQueryKeyInternal(path, arg1, "query");
			},
			queryFilter: () => {
				return {
					...arg2,
					queryKey: getQueryKeyInternal(path, arg1, "query")
				};
			},
			infiniteQueryOptions: () => {
				return trpcInfiniteQueryOptions({
					input: arg1,
					opts: arg2,
					path,
					queryClient: opts.queryClient,
					queryKey: getQueryKeyInternal(path, arg1, "infinite"),
					query: callIt("query")
				});
			},
			infiniteQueryKey: () => {
				return getQueryKeyInternal(path, arg1, "infinite");
			},
			infiniteQueryFilter: () => {
				return {
					...arg2,
					queryKey: getQueryKeyInternal(path, arg1, "infinite")
				};
			},
			mutationOptions: () => {
				return trpcMutationOptions({
					opts: arg1,
					path,
					queryClient: opts.queryClient,
					mutate: callIt("mutation"),
					overrides: opts.overrides?.mutations
				});
			},
			mutationKey: () => {
				return getMutationKeyInternal(path);
			},
			subscriptionOptions: () => {
				return trpcSubscriptionOptions({
					opts: arg2,
					path,
					queryKey: getQueryKeyInternal(path, arg1, "any"),
					subscribe: callIt("subscription")
				});
			}
		};
		return contextMap[utilName]();
	});
}

//#endregion
//#region src/internals/Context.tsx
/**
* Create a set of type-safe provider-consumers
*
* @see https://trpc.io/docs/client/tanstack-react-query/setup#3a-setup-the-trpc-context-provider
*/
function createTRPCContext() {
	const TRPCClientContext = React.createContext(null);
	const TRPCContext = React.createContext(null);
	function TRPCProvider(props) {
		const value = React.useMemo(() => createTRPCOptionsProxy({
			client: props.trpcClient,
			queryClient: props.queryClient
		}), [props.trpcClient, props.queryClient]);
		return /* @__PURE__ */ jsx(TRPCClientContext.Provider, {
			value: props.trpcClient,
			children: /* @__PURE__ */ jsx(TRPCContext.Provider, {
				value,
				children: props.children
			})
		});
	}
	function useTRPC() {
		const utils = React.useContext(TRPCContext);
		if (!utils) throw new Error("useTRPC() can only be used inside of a <TRPCProvider>");
		return utils;
	}
	function useTRPCClient() {
		const client = React.useContext(TRPCClientContext);
		if (!client) throw new Error("useTRPCClient() can only be used inside of a <TRPCProvider>");
		return client;
	}
	return {
		TRPCProvider,
		useTRPC,
		useTRPCClient
	};
}

//#endregion
export { createTRPCContext, createTRPCOptionsProxy, useSubscription };
//# sourceMappingURL=index.mjs.map